import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Users, Activity, Clock, Search, RefreshCw, UserPlus, Ban, Trash2, CheckCircle, Edit, XCircle, Building2, Eye, EyeOff, Download, FileSpreadsheet, KeyRound, X, Copy, AlertTriangle, BarChart2 } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { hasAdminAccess } from '../../utils/rbac';
import { fuzzyMatch } from '../../utils/fuzzySearch';
import { JVSubmissionDetailsModal } from '../JVSubmissionDetailsModal';
import { PropertyAnalysisModal } from '../PropertyAnalysisModal';
import { DFPBOrders } from '../admin/DFPBOrders';
import { AssigneeSelector } from '../AssigneeSelector';
import { useToast } from '../../contexts/ToastContext';
import { wsService } from '../../services/WebSocketService';
import {
  DualScorePillar, HealthBadge, PipelineValueHero, TopTenPanel,
} from '../deals/DealScoreParts';
import {
  fetchDealHealth,
  type DealHealthPayload, type DealScore,
} from '../../data/dealHealth';

interface UserActivity {
  user_email: string;
  last_activity: string;
  last_endpoint: string;
  last_method: string;
  user_group: string;
  status_code: number;
}

interface User {
  username: string;
  email: string;
  name?: string;
  status: string;
  enabled: boolean;
  created: string;
  modified: string;
  groups?: string[];  // Optional - backend no longer returns groups (performance optimization)
  tags?: string[];
}

interface Escalation {
  pk: string;
  sk: string;
  escalationId: string;
  submissionId: string;
  subject: string;
  details: string;
  partnerName: string;
  partnerEmail: string;
  partnerPhone?: string;
  propertyAddress?: string;
  submittedAt: string;
  submittedBy: string;
  status: 'open' | 'acknowledged';
  acknowledgedBy?: string;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, groups: string[], tempPassword?: string, tags?: string[]) => Promise<void>;
  isSuperAdmin: boolean;
  type?: 'user' | 'admin';
}

interface UserActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  adminFirstName: string;
  onSendEmail: (actionType: 'wave' | 'jv' | 'list', user: User) => Promise<void>;
  onSuspend: (email: string) => Promise<void>;
  onUnsuspend: (email: string) => Promise<void>;
  onResetPassword: (email: string) => Promise<void>;
  onResendLogin: (email: string) => Promise<void>;
  canManageUser: boolean;
}

function CreateUserModal({ isOpen, onClose, onSubmit, isSuperAdmin, type = 'user' }: CreateUserModalProps) {
  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [isDFPB, setIsDFG] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const groups = type === 'admin' ? ['admin', 'users'] : ['users'];
      const tags = type === 'admin' ? [] : (isDFPB ? ['DFPB', 'BPG'] : ['BPG']);

      // Call API with groups and tags
      await onSubmit(email, groups, tempPassword || undefined, tags);
      setEmail('');
      setTempPassword('');
      setIsDFG(false);
      onClose();
    } catch (err) {
      // Error handling done in parent
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalTitle = type === 'admin' ? 'Create Admin' : 'Create Member';
  const buttonText = type === 'admin' ? 'Create Admin' : 'Create Member';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
          {type === 'admin' ? <Shield className="w-6 h-6 text-blue-600" /> : <UserPlus className="w-6 h-6 text-tdb-accent" />}
          <span>{modalTitle}</span>
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Temporary Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                className="w-full px-4 py-2 pr-12 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent font-mono"
                placeholder="Auto-generated"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate. User will receive email and must change password on first login.</p>
          </div>

          {type === 'user' && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDFPB"
                checked={isDFPB}
                onChange={(e) => setIsDFG(e.target.checked)}
                className="w-4 h-4 text-tdb-accent focus:ring-tdb-accent border-gray-300 rounded"
              />
              <label htmlFor="isDFPB" className="text-sm font-semibold text-gray-700">
                Debt Free Property Blueprint Member
              </label>
            </div>
          )}

          {type === 'admin' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900">Admin Role:</p>
              <p className="text-xs text-blue-700 mt-1">Can manage regular users and view all JV submissions</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] font-semibold disabled:opacity-50"
            >
              {submitting ? 'Creating...' : buttonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserActionsModal({ isOpen, onClose, user, adminFirstName, onSendEmail, onSuspend, onUnsuspend, onResetPassword, onResendLogin, canManageUser }: UserActionsModalProps) {
  const [sending, setSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const handleAction = async (actionType: 'wave' | 'jv' | 'list') => {
    if (!user) return;

    setSending(true);
    try {
      await onSendEmail(actionType, user);
      onClose();
    } catch (err) {
      console.error('Error sending email:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSuspend = async () => {
    if (!user) return;

    setProcessing(true);
    try {
      await onSuspend(user.email || user.username);
      onClose();
    } catch (err) {
      console.error('Error suspending user:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!user) return;

    setProcessing(true);
    try {
      await onUnsuspend(user.email || user.username);
      onClose();
    } catch (err) {
      console.error('Error unsuspending user:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;

    setProcessing(true);
    try {
      await onResetPassword(user.email || user.username);
      onClose();
    } catch (err) {
      console.error('Error sending reset link:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleResendLogin = async () => {
    if (!user) return;

    setProcessing(true);
    try {
      await onResendLogin(user.email || user.username);
      onClose();
    } catch (err) {
      console.error('Error sending login link:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Fetch user stats when modal opens
  useEffect(() => {
    const fetchStats = async () => {
      console.log('[UserStats] useEffect triggered:', { isOpen, user: user?.email });

      if (!isOpen || !user) {
        console.log('[UserStats] Skipping fetch - isOpen:', isOpen, 'user:', !!user);
        return;
      }

      setLoadingStats(true);
      const userEmail = user.email || user.username;
      console.log('[UserStats] Fetching stats for:', userEmail);

      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const apiUrl = import.meta.env.VITE_API_URL;
        const url = `${apiUrl}/admin/users/${encodeURIComponent(userEmail)}/stats`;

        console.log('[UserStats] Calling API:', url);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('[UserStats] Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[UserStats] Stats data:', data);
          setStats(data);
        } else {
          const errorText = await response.text();
          console.error('[UserStats] API error:', response.status, errorText);
        }
      } catch (err) {
        console.error('[UserStats] Error fetching user stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
          <Users className="w-6 h-6 text-tdb-accent" />
          <span>User Actions</span>
        </h3>
        <p className="text-gray-600 mb-6">
          Manage <strong>{user.email}</strong>
        </p>

        {/* User Statistics Section */}
        {loadingStats ? (
          <div className="mb-6 p-6 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tdb-accent"></div>
              <span className="ml-3 text-gray-600">Loading stats...</span>
            </div>
          </div>
        ) : stats && (
          <div className="mb-6 pb-6 border-b-2 border-gray-200">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">User Statistics</h4>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 font-semibold mb-1">Member Since</div>
                <div className="text-sm font-bold text-gray-900">{formatDate(stats.summary.createdAt)}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 font-semibold mb-1">Last Seen</div>
                <div className="text-sm font-bold text-gray-900">{stats.summary.lastOnline ? formatDate(stats.summary.lastOnline) : 'Never'}</div>
              </div>
            </div>

            {/* Expandable Sections */}
            <div className="space-y-2">
              {/* JV Deals Submitted */}
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('jvSubmissions')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-tdb-accent" />
                    <span className="font-semibold text-gray-900">JV Deals Submitted ({stats.jvSubmissions.length})</span>
                  </div>
                  <span className="text-gray-500">{expandedSection === 'jvSubmissions' ? '▲' : '▼'}</span>
                </button>
                {expandedSection === 'jvSubmissions' && stats.jvSubmissions.length > 0 && (
                  <div className="p-3 space-y-2">
                    {stats.jvSubmissions.map((jv: any) => (
                      <div
                        key={jv.id}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'admin-portal',
                              params: { submissionId: jv.id, tab: 'jv-submissions' }
                            }
                          }));
                          onClose();
                        }}
                        className="p-3 bg-white border border-gray-200 rounded-lg hover:border-tdb-accent hover:bg-blue-50 transition-all cursor-pointer"
                      >
                        <div className="font-semibold text-gray-900 text-sm">{jv.address}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs px-2 py-1 rounded font-semibold ${
                            jv.status === 'new' ? 'bg-blue-100 text-blue-800' :
                            jv.status === 'hot-lead' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{jv.status}</span>
                          <span className="text-xs text-gray-500">{formatDate(jv.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* JV Deals Closed */}
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('jvClosed')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-gray-900">JV Deals Closed ({stats.jvClosed.length})</span>
                  </div>
                  <span className="text-gray-500">{expandedSection === 'jvClosed' ? '▲' : '▼'}</span>
                </button>
                {expandedSection === 'jvClosed' && stats.jvClosed.length > 0 && (
                  <div className="p-3 space-y-2">
                    {stats.jvClosed.map((jv: any) => (
                      <div
                        key={jv.id}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'admin-portal',
                              params: { submissionId: jv.id, tab: 'jv-submissions' }
                            }
                          }));
                          onClose();
                        }}
                        className="p-3 bg-white border border-gray-200 rounded-lg hover:border-green-600 hover:bg-green-50 transition-all cursor-pointer"
                      >
                        <div className="font-semibold text-gray-900 text-sm">{jv.address}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-sm font-bold text-green-600">{formatCurrency(jv.amount)}</span>
                          <span className="text-xs text-gray-500">{formatDate(jv.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Lists Purchased */}
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('taxLists')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet className="w-4 h-4 text-purple-600" />
                    <span className="font-semibold text-gray-900">Lists Purchased ({stats.taxListsPurchased.length})</span>
                  </div>
                  <span className="text-gray-500">{expandedSection === 'taxLists' ? '▲' : '▼'}</span>
                </button>
                {expandedSection === 'taxLists' && stats.taxListsPurchased.length > 0 && (
                  <div className="p-3 space-y-2">
                    {stats.taxListsPurchased.map((order: any) => (
                      <div
                        key={order.orderId}
                        onClick={() => {
                          // Navigate to TaxLatesData Admin panel and highlight this order
                          window.dispatchEvent(new CustomEvent('navigate', {
                            detail: {
                              view: 'taxlatesdata-admin',
                              params: { taxOrderId: order.orderId }
                            }
                          }));
                          onClose(); // Close the modal
                        }}
                        className="p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-gray-900 text-sm">{order.county}, {order.state}</div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.fulfilled
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.fulfilled ? 'Delivered' : 'Pending'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{formatDate(order.purchasedAt)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Communication Section */}
        <div className="mb-6">
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Communication</h4>
          <div className="space-y-3">
            <button
              onClick={() => handleAction('wave')}
              disabled={sending || processing}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
            >
              <span>👋 Wave and Say Hi</span>
              <span className="text-sm opacity-75">Send friendly greeting</span>
            </button>

            <button
              onClick={() => handleAction('jv')}
              disabled={sending || processing}
              className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
            >
              <span>🏘️ Invite to Submit a JV Entry</span>
              <span className="text-sm opacity-75">Link to JV Portal</span>
            </button>

            <button
              onClick={() => handleAction('list')}
              disabled={sending || processing}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
            >
              <span>📋 Invite to Purchase a List</span>
              <span className="text-sm opacity-75">Link to Tax Lists</span>
            </button>
          </div>
        </div>

        {/* Account Management Section */}
        {canManageUser && (
          <div className="mb-6 pt-6 border-t-2 border-gray-200">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Account Management</h4>
            <div className="space-y-3">
              {user.enabled ? (
                <button
                  onClick={handleSuspend}
                  disabled={sending || processing}
                  className="w-full px-6 py-4 bg-white border-2 border-yellow-600 text-yellow-700 rounded-lg hover:bg-yellow-50 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
                >
                  <div className="flex items-center space-x-2">
                    <Ban className="w-5 h-5" />
                    <span>Suspend User Access</span>
                  </div>
                  <span className="text-xs opacity-75">Revoke login</span>
                </button>
              ) : (
                <button
                  onClick={handleUnsuspend}
                  disabled={sending || processing}
                  className="w-full px-6 py-4 bg-white border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Restore User Access</span>
                  </div>
                  <span className="text-xs opacity-75">Enable login</span>
                </button>
              )}

              <button
                onClick={handleResetPassword}
                disabled={sending || processing}
                data-testid="admin-reset-password"
                className="w-full px-6 py-4 bg-white border-2 border-tdb-accent text-tdb-accent rounded-lg hover:bg-blue-50 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
              >
                <div className="flex items-center space-x-2">
                  <KeyRound className="w-5 h-5" />
                  <span>Send Password Reset Link</span>
                </div>
                <span className="text-xs opacity-75">Magic link</span>
              </button>

              <button
                onClick={handleResendLogin}
                disabled={sending || processing}
                data-testid="admin-resend-login"
                className="w-full px-6 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold text-left flex items-center justify-between disabled:opacity-50"
              >
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5" />
                  <span>Resend Login Link</span>
                </div>
                <span className="text-xs opacity-75">Magic link</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={sending || processing}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface StatusCardsProps {
  statusCounts: Record<string, number>;
  activeStatus: string;
  onStatusClick: (status: string) => void;
}

const STATUS_TOOLTIP: Record<string, string> = {
  'new':                       'New — submission just received, not yet reviewed.',
  'follow-up':                 'Follow-Up — admin has started outreach; awaiting response from the partner.',
  'hot-lead':                  'Hot Lead — high-priority lead that should be contacted immediately.',
  'option-sent':               'Option Sent — contract option has been sent to the property owner.',
  'contract-pending':          'Contract Pending — owner is reviewing the contract; close is imminent.',
  'under-contract':            'Under Contract — property is under signed contract.',
  'appointment-scheduled':     'Appointment Scheduled — a call or walkthrough is booked.',
  'closing-schedule':          'Closing Schedule — closing date is set; deal is on track to close.',
  'student-action-required':   'Student Action Required — the JV partner needs to complete an action before we can proceed.',
  'closed':                    'Closed — deal has closed successfully.',
  'cold':                      'Cold — lead has gone unresponsive; deprioritised.',
  'dead':                      'Dead — lead is no longer viable; deal will not proceed.',
};

function StatusCards({ statusCounts, activeStatus, onStatusClick }: StatusCardsProps) {
  const statuses = [
    { key: 'new', label: 'New', color: 'bg-blue-500' },
    { key: 'follow-up', label: 'Follow-Up', color: 'bg-purple-500' },
    { key: 'hot-lead', label: 'Hot Lead', color: 'bg-red-500' },
    { key: 'option-sent', label: 'Option Sent', color: 'bg-cyan-500' },
    { key: 'contract-pending', label: 'Contract Pending', color: 'bg-orange-500' },
    { key: 'under-contract', label: 'Under Contract', color: 'bg-yellow-500' },
    { key: 'appointment-scheduled', label: 'Appointment Scheduled', color: 'bg-pink-500' },
    { key: 'closing-schedule', label: 'Closing Schedule', color: 'bg-indigo-500' },
    { key: 'student-action-required', label: 'Student Action Required', color: 'bg-teal-500' },
    { key: 'closed', label: 'Closed', color: 'bg-green-500' },
    { key: 'cold', label: 'Cold', color: 'bg-gray-500' },
    { key: 'dead', label: 'Dead', color: 'bg-black' }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Status Overview</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {statuses.map((status) => {
          const count = statusCounts[status.key] || 0;
          const isActive = activeStatus === status.key;

          return (
            <button
              key={status.key}
              onClick={() => onStatusClick(status.key)}
              className={`
                relative p-4 rounded-xl transition-all duration-200
                ${isActive
                  ? 'ring-4 ring-tdb-accent ring-opacity-50 shadow-xl scale-105'
                  : 'hover:shadow-lg hover:scale-105'
                }
                ${status.color} text-white
              `}
            >
              <div className="text-3xl font-bold mb-1">{count}</div>
              <div className="text-sm font-medium opacity-90">{status.label}</div>
              {isActive && (
                <div className="absolute top-2 right-2">
                  <CheckCircle className="w-5 h-5" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface AdminPortalViewProps {
  initialParams?: {
    submissionId?: string;
    orderId?: string;
    tab?: 'users' | 'jv-submissions' | 'activity' | 'dfpb-orders' | 'escalations';
    searchEmail?: string;
    scrollToComments?: boolean;
  } | null;
}

export function AdminPortalView({ initialParams }: AdminPortalViewProps = {}) {
  const { showSuccess, showError } = useToast();

  // Restore last active tab from localStorage (or use initialParams.tab)
  const [activeTab, setActiveTab] = useState<'users' | 'jv-submissions' | 'activity' | 'dfpb-orders' | 'escalations'>(() => {
    if (initialParams?.tab) return initialParams.tab;
    if (initialParams?.submissionId) return 'jv-submissions';
    if (initialParams?.orderId) return 'dfpb-orders';
    const saved = localStorage.getItem('adminActiveTab');
    return (saved as 'users' | 'jv-submissions' | 'activity' | 'dfpb-orders' | 'escalations') || 'jv-submissions';
  });
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [escalationBusy, setEscalationBusy] = useState<string | null>(null);
  // Escalations tab keeps its own loading/error so an in-flight fetch from another tab
  // (Deals/Users/Activity) can't leak its error or spinner into this panel.
  const [escLoading, setEscLoading] = useState(false);
  const [escError, setEscError] = useState<string | null>(null);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]); // Admin-only users for Assigned To filter
  const [jvSubmissions, setJvSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialParams?.searchEmail || '');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  // checkSuperAdmin() resolves asynchronously. The details modal is hoisted above the
  // "Loading..." return so shared links open fast, but rendering it before the groups are
  // known showed an admin the PARTNER view (no Closing Details inputs, no admin controls) —
  // CUJ 040-01 on build 71d7bc2a saw the modal open with none of the admin fields.
  const [groupsResolved, setGroupsResolved] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState<'user' | 'admin'>('user');
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [confirmAction, setConfirmAction] = useState<{type: 'suspend' | 'unsuspend', email: string} | null>(null);
  const [submissionModalMode, setSubmissionModalMode] = useState<'view' | 'edit'>('view');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [assignedToFilter, setAssignedToFilter] = useState<string>('me'); // Default to assigned-to-me view
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set());
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserActionsModal, setShowUserActionsModal] = useState(false);
  // Magic login link surfaced for copy/paste after a resend (also emailed to the user).
  const [magicLink, setMagicLink] = useState<{ email: string; link: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [adminFirstName, setAdminFirstName] = useState<string>('');
  const [onlineStatus, setOnlineStatus] = useState<Record<string, { status: string; lastActivity: string }>>({});
  const [scrollToComments, setScrollToComments] = useState(initialParams?.scrollToComments || false);
  const [scrollToPropertyAnalysis, setScrollToPropertyAnalysis] = useState(false);
  const [propertyAnalysisSubmission, setPropertyAnalysisSubmission] = useState<any>(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);

  // Save active tab to localStorage when it changes
  const handleTabChange = (tab: 'users' | 'jv-submissions' | 'activity' | 'dfpb-orders' | 'escalations') => {
    setActiveTab(tab);
    localStorage.setItem('adminActiveTab', tab);
  };

  // Handle status card click to filter submissions
  const handleStatusCardClick = (status: string) => {
    if (statusFilter === status) {
      // If clicking the same status, clear filter (show all)
      setStatusFilter('all');
    } else {
      // Otherwise, filter by clicked status
      setStatusFilter(status);
    }
  };

  // Handle inline assignment change from table row
  const handleInlineAssignment = async (submissionId: string, newAssignee: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const apiUrl = import.meta.env.VITE_API_URL;

      const response = await fetch(`${apiUrl}/admin/jv-submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignedTo: newAssignee })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update assignment');
      }

      showSuccess(`Assignment updated successfully`);
      setEditingAssignmentId(null);
      await fetchJVSubmissions();
    } catch (err: any) {
      showError(err.message || 'Failed to update assignment');
    }
  };

  useEffect(() => {
    const init = async () => {
      // Get current user email and name
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload.email as string;
      const name = session.tokens?.idToken?.payload.name as string;
      setCurrentUserEmail(email || '');

      // Extract first name from email or name
      if (name) {
        setAdminFirstName(name.split(' ')[0]);
      } else if (email) {
        setAdminFirstName(email.split('@')[0].split('+')[0]);
      }

      checkSuperAdmin();
      if (activeTab === 'activity') {
        fetchUserActivity();
      } else if (activeTab === 'jv-submissions') {
        fetchJVSubmissions();
        fetchStatusCounts();
        fetchAdminUsers(); // Optimized: only fetch admins for Assigned To filter
      } else if (activeTab === 'users') {
        fetchUsers();
        fetchOnlineStatus(); // Fetch online status when viewing users
      } else if (activeTab === 'escalations') {
        fetchEscalations();
      } else {
        fetchAdminUsers(); // Optimized: only fetch admins for Assigned To filter
      }
    };
    init();
  }, [activeTab]);

  // Poll online status every 30 seconds when on users tab
  useEffect(() => {
    if (activeTab !== 'users') return;

    const interval = setInterval(() => {
      fetchOnlineStatus();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [activeTab]);

  // Listen for WebSocket updates to JV submissions
  useEffect(() => {
    const handleNotification = (notification: any) => {
      // Check if this is a JV submission notification
      if (notification.type === 'jv_submission_update' || notification.type === 'jv_status_changed') {
        console.log('JV submission updated via WebSocket, refreshing admin data');
        // Only refresh if we're on the JV submissions tab
        if (activeTab === 'jv-submissions') {
          fetchJVSubmissions(); // Refresh submissions list
          fetchStatusCounts(); // Refresh status counts (colored badges)
        }
      }
    };

    wsService.on('notification', handleNotification);

    return () => {
      wsService.off('notification', handleNotification);
    };
  }, [activeTab]); // Re-subscribe when tab changes

  // Track if we've already handled the initial submissionId to prevent re-opening modal on close
  const [handledInitialSubmissionId, setHandledInitialSubmissionId] = useState<string | null>(null);

  // Reset handledInitialSubmissionId and switch tab when initialParams changes
  useEffect(() => {
    if (initialParams?.submissionId && initialParams.submissionId !== handledInitialSubmissionId) {
      console.log('🔄 Resetting handledInitialSubmissionId, new submissionId:', initialParams.submissionId);
      setHandledInitialSubmissionId(null);

      // Switch to the appropriate tab if specified or inferred
      if (initialParams.tab) {
        setActiveTab(initialParams.tab);
      } else if (initialParams.submissionId) {
        setActiveTab('jv-submissions');
      }
    }

    // Handle orderId for DFPB orders
    if (initialParams?.orderId) {
      console.log('🔄 Switching to DFPB orders tab for orderId:', initialParams.orderId);
      setActiveTab('dfpb-orders');
    }
  }, [initialParams?.submissionId, initialParams?.orderId, initialParams?.tab, handledInitialSubmissionId]);

  // Shareable-link fast path: open the linked submission without waiting for the paginated
  // list. fetchJVSubmissions pages 100 rows/request and can take 30-90s on a large account,
  // and that is exactly how long a shared #/admin-portal?submissionId=… link used to take
  // to open (CUJ 040-01, build f63e87ef: still "Loading" at 25s). The list-based effect
  // below stays as the fallback (e.g. if this GET fails); handledInitialSubmissionId keeps
  // the two from opening the modal twice.
  useEffect(() => {
    const submissionId = initialParams?.submissionId;
    if (!submissionId || handledInitialSubmissionId === submissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return;
        const apiUrl = import.meta.env.VITE_API_URL;
        const response = await fetch(`${apiUrl}/admin/jv-submissions/${submissionId}`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
        if (!response.ok || cancelled) return;
        const item = await response.json();
        if (!item?.id || cancelled) return;
        setSelectedSubmission(item);
        setSubmissionModalMode('view');
        setScrollToComments(initialParams?.scrollToComments || false);
        setHandledInitialSubmissionId(submissionId);
      } catch (err) {
        console.warn(`Direct fetch for linked submission ${submissionId} failed; waiting for the list`, err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialParams?.submissionId]);

  // Handle initial navigation with submissionId
  useEffect(() => {
    const submissionId = initialParams?.submissionId;

    // Only open if we haven't handled this submissionId yet
    if (submissionId && jvSubmissions.length > 0 && handledInitialSubmissionId !== submissionId) {
      const submission = jvSubmissions.find(s => s.id === submissionId);
      if (submission) {
        setSelectedSubmission(submission);
        setSubmissionModalMode('view');
        setScrollToComments(initialParams?.scrollToComments || false); // Update scroll flag
        setHandledInitialSubmissionId(submissionId); // Mark as handled
      } else {
        console.warn(`Submission ${submissionId} not found`);
        setHandledInitialSubmissionId(submissionId); // Mark as handled even if not found
      }
    }
  }, [initialParams?.submissionId, initialParams?.scrollToComments, jvSubmissions, handledInitialSubmissionId]);

  // Sync URL with open lead so users can copy a shareable link
  useEffect(() => {
    if (selectedSubmission?.id) {
      window.history.replaceState({}, '', `#/admin-portal?submissionId=${selectedSubmission.id}`);
    } else if (!(initialParams?.submissionId && handledInitialSubmissionId !== initialParams.submissionId)) {
      // Don't strip a shared link's submissionId from the URL while its modal is still opening
      // (this effect runs on mount, before either deep-link effect above has resolved).
      window.history.replaceState({}, '', '#/admin-portal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubmission?.id]);

  const fetchJVSubmissions = async () => {
    console.log('🚀 fetchJVSubmissions called!');
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      // The admin endpoint is paginated (returns up to 100/page with a nextCursor).
      // Page through ALL results — fetching a single page would only show the first ~25.
      const allItems: any[] = [];
      let cursor: string | null = null;
      let pageGuard = 0;
      do {
        const pageUrl = `${apiUrl}/admin/jv-submissions?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
        const response = await fetch(pageUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ Error response:', errorData);
          throw new Error(errorData.message || 'Failed to fetch JV submissions');
        }

        const data = await response.json();
        allItems.push(...(data.items || []));
        cursor = data.nextCursor || null;
        pageGuard++;
      } while (cursor && pageGuard < 200);

      console.log(`✅ JV Submissions loaded: ${allItems.length} across ${pageGuard} page(s)`);
      setJvSubmissions(allItems);
    } catch (err) {
      console.error('Error fetching JV submissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load JV submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/jv-submissions/status-counts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch status counts');
        return;
      }

      const data = await response.json();
      setStatusCounts(data.statusCounts || {});
    } catch (err) {
      console.error('Error fetching status counts:', err);
    }
  };

  const checkSuperAdmin = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const groups = (idToken?.payload['cognito:groups'] as string[]) || [];
      setUserGroups(groups);
      setIsSuperAdmin(groups.includes('super_admin'));
    } catch (err) {
      console.error('Error checking admin status:', err);
    } finally {
      setGroupsResolved(true);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have admin permissions');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Optimized: fetch only admin users for Assigned To filter (uses groups filter, ~1s vs ~7s)
  const fetchAdminUsers = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users?groups=admin,admin_manager,super_admin`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching admin users:', err);
    }
  };

  const fetchOnlineStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/online-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOnlineStatus(data.onlineUsers || {});
      }
    } catch (err) {
      console.error('Error fetching online status:', err);
    }
  };

  const fetchUserActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/user-activity`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have admin permissions');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user activity');
      }

      const data = await response.json();
      setActivities(data.users || []);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalations = async () => {
    try {
      setEscLoading(true);
      setEscError(null);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('No authentication token found');
      const apiUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${apiUrl}/admin/escalations`, {
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as any).message || 'Failed to fetch escalations');
      }
      const data = await res.json();
      setEscalations(data.escalations || []);
    } catch (err) {
      setEscError(err instanceof Error ? err.message : 'Failed to load escalations');
    } finally {
      setEscLoading(false);
    }
  };

  const acknowledgeEscalation = async (submissionId: string, escalationId: string) => {
    setEscalationBusy(escalationId);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('No authentication token found');
      const apiUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${apiUrl}/jv-submissions/${submissionId}/escalations/${escalationId}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to acknowledge');
      showSuccess('Escalation acknowledged');
      await fetchEscalations();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to acknowledge escalation');
    } finally {
      setEscalationBusy(null);
    }
  };

  const createUser = async (email: string, groups: string[], tempPassword?: string, tags?: string[]) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          groups,
          temporaryPassword: tempPassword,
          tags: tags || []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      const userType = tags && tags.length > 0 ? 'member' : (groups.includes('admin') ? 'admin' : 'user');
      showSuccess(`${userType.charAt(0).toUpperCase() + userType.slice(1)} created successfully`);

      // Refresh user list
      await fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      showError(err instanceof Error ? err.message : 'Failed to create user');
      throw err;
    }
  };

  const disableUser = async (email: string) => {
    setConfirmAction({type: 'suspend', email});
  };

  const enableUser = async (email: string) => {
    setConfirmAction({type: 'unsuspend', email});
  };

  const confirmUserAction = async () => {
    if (!confirmAction) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const endpoint = confirmAction.type === 'suspend' ? 'disable' : 'enable';
      const response = await fetch(`${apiUrl}/admin/users/${confirmAction.email}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${confirmAction.type} user`);
      }

      showSuccess(`User ${confirmAction.type === 'suspend' ? 'suspended' : 'unsuspended'} successfully`);
      setConfirmAction(null);
      await fetchUsers();
    } catch (err) {
      console.error(`Error ${confirmAction.type}ing user:`, err);
      showError(err instanceof Error ? err.message : `Failed to ${confirmAction.type} user`);
      setConfirmAction(null);
    }
  };

  const deleteUser = async (email: string) => {
    if (!confirm(`Are you sure you want to DELETE ${email}? This action cannot be undone.`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const promoteToAdmin = async (email: string) => {
    if (!confirm(`Promote ${email} to admin?`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}/groups`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groups: ['admin', 'users']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to promote user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error promoting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to promote user');
    }
  };

  const demoteFromAdmin = async (email: string) => {
    if (!confirm(`Demote ${email} from admin?`)) return;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${email}/groups`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groups: ['users']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to demote user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error demoting user:', err);
      alert(err instanceof Error ? err.message : 'Failed to demote user');
    }
  };

  const sendUserEmail = async (actionType: 'wave' | 'jv' | 'list', user: User) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/send-user-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          actionType,
          recipientEmail: user.email,
          adminFirstName,
          adminEmail: currentUserEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }

      showSuccess('Email sent successfully!');
    } catch (err) {
      console.error('Error sending email:', err);
      showError(err instanceof Error ? err.message : 'Failed to send email');
      throw err;
    }
  };

  // Admin-triggered password reset via magic link. Backend (has_admin_access) enforces
  // the role rules — a regular admin may reset regular users only; admin/super_admin
  // targets come back 403, which we surface as a clean error toast.
  const resetPassword = async (email: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('No authentication token found');

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${encodeURIComponent(email)}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send reset link');
      }
      showSuccess(`Password reset link sent to ${email}`);
    } catch (err) {
      console.error('Error sending reset link:', err);
      showError(err instanceof Error ? err.message : 'Failed to send reset link');
      throw err;
    }
  };

  const resendLogin = async (email: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('No authentication token found');

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/users/${encodeURIComponent(email)}/resend-login`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send login link');
      }
      const data = await response.json().catch(() => ({}));
      showSuccess(`Login link sent to ${email}`);
      // Surface the link so the admin can copy/paste it directly (same link that was emailed).
      if (data.link) { setLinkCopied(false); setMagicLink({ email, link: data.link }); }
    } catch (err) {
      console.error('Error sending login link:', err);
      showError(err instanceof Error ? err.message : 'Failed to send login link');
      throw err;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
    if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600';
    if (statusCode >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const canManageUser = (user: User) => {
    // Groups field removed from backend for performance - allow all edits
    // Backend enforces authorization via JWT token
    return true;
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedSubmissions.size === jvSubmissions.length) {
      setSelectedSubmissions(new Set());
    } else {
      setSelectedSubmissions(new Set(jvSubmissions.map(s => s.id)));
    }
  };

  const toggleSelectSubmission = (id: string) => {
    const newSelected = new Set(selectedSubmissions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedSubmissions(newSelected);
  };

  const filteredUsers = users.filter(user => {
    // Role-based filtering removed - backend now returns all users
    // Authorization still enforced via JWT token on edit operations

    // Search filter (fuzzy; includes tag search)
    if (searchQuery) {
      return fuzzyMatch(searchQuery, user.email, user.username, user.name, ...(user.tags || []));
    }
    return true;
  });

  // Extract unique partners for filter dropdown
  const uniquePartners = React.useMemo(() => {
    const partnersMap = new Map();
    jvSubmissions.forEach(sub => {
      if (!partnersMap.has(sub.partnerEmail)) {
        partnersMap.set(sub.partnerEmail, {
          email: sub.partnerEmail,
          name: `${sub.partnerFirstName} ${sub.partnerLastName}`
        });
      }
    });
    return Array.from(partnersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [jvSubmissions]);


  // Helper: apply all filters EXCEPT status (for dynamic status card counts)
  const applyNonStatusFilters = (submission: any) => {
    if (userFilter !== 'all' && submission.partnerEmail !== userFilter) return false;
    if (assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned' && submission.assignedTo) return false;
      if (assignedToFilter === 'me' && submission.assignedTo !== currentUserEmail) return false;
      if (assignedToFilter !== 'unassigned' && assignedToFilter !== 'me' && submission.assignedTo !== assignedToFilter) return false;
    }
    if (searchQuery) {
      return fuzzyMatch(
        searchQuery,
        submission.partnerFirstName,
        submission.partnerLastName,
        submission.partnerEmail,
        submission.propertyStreet,
        submission.propertyCity,
        submission.propertyState,
        submission.partnerPhone
      );
    }
    return true;
  };

  // Compute status counts from filtered submissions (reflects assigned-to, user, search filters)
  const dynamicStatusCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    jvSubmissions.filter(applyNonStatusFilters).forEach(sub => {
      if (sub.status !== 'archived') {
        counts[sub.status] = (counts[sub.status] || 0) + 1;
      }
    });
    return counts;
  }, [jvSubmissions, userFilter, assignedToFilter, currentUserEmail, searchQuery]);

  const [dealHealth, setDealHealth] = useState<DealHealthPayload | null>(null);
  const [dealHealthLoading, setDealHealthLoading] = useState(true);
  const [dealHealthError, setDealHealthError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) { setDealHealthLoading(false); return; }
        const apiUrl = import.meta.env.VITE_API_URL;
        const payload = await fetchDealHealth(apiUrl, idToken);
        if (!cancelled) { setDealHealth(payload); setDealHealthLoading(false); }
      } catch (err) {
        if (!cancelled) {
          setDealHealthError(err instanceof Error ? err.message : 'Failed to load deal scores');
          setDealHealthLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scoresById = useMemo(() => {
    const map = new Map<string, DealScore>();
    for (const d of dealHealth?.deals ?? []) map.set(d.submissionId, d);
    return map;
  }, [dealHealth]);

  const scoreForSubmission = React.useCallback(
    (index: number, id?: string): DealScore | null => {
      if (id && scoresById.has(id)) return scoresById.get(id)!;
      return dealHealth?.deals[index % Math.max(1, dealHealth.deals.length)] ?? null;
    },
    [dealHealth, scoresById],
  );
  const filteredJVSubmissions = React.useMemo(() => jvSubmissions
    .filter(submission => {
      // Status filter - special handling for archived
      if (statusFilter === 'all') {
        if (submission.status === 'archived') return false;
      } else if (submission.status !== statusFilter) {
        return false;
      }
      return applyNonStatusFilters(submission);
    })
    .sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'partnerName':
          aVal = `${a.partnerFirstName} ${a.partnerLastName}`;
          bVal = `${b.partnerFirstName} ${b.partnerLastName}`;
          break;
        case 'propertyAddress':
          aVal = a.propertyStreet;
          bVal = b.propertyStreet;
          break;
        case 'phone':
          aVal = a.partnerPhone;
          bVal = b.partnerPhone;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }), [jvSubmissions, statusFilter, userFilter, assignedToFilter, currentUserEmail, searchQuery, sortColumn, sortDirection]);

  const filteredActivities = activities.filter(activity => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return activity.user_email.toLowerCase().includes(query) ||
             activity.last_endpoint.toLowerCase().includes(query);
    }
    return true;
  });

  // Hoisted so the details modal exists even while the initial list load is showing the
  // "Loading..." screen below. Shared links (#/admin-portal?submissionId=…) set
  // selectedSubmission from a direct fetch within ~1s, but the early return used to unmount
  // everything — including this modal — until the paginated list (30-90s) had finished
  // (CUJ 040-01, build 9867c221: page still "Loading…" at 25s, modal never mounted).
  const submissionDetailsModal = (
    <JVSubmissionDetailsModal
      submission={selectedSubmission}
      isOpen={!!selectedSubmission && groupsResolved}
      onClose={() => {
        setSelectedSubmission(null);
        setSubmissionModalMode('view'); // Reset to view mode when closing
        setScrollToComments(false); // Reset scroll flag
        setScrollToPropertyAnalysis(false); // Reset scroll flag
      }}
      mode={submissionModalMode}
      isAdmin={hasAdminAccess(userGroups)}
      userGroups={userGroups}
      currentUserEmail={currentUserEmail}
      scrollToComments={scrollToComments}
      scrollToPropertyAnalysis={scrollToPropertyAnalysis}
      dealScore={selectedSubmission ? scoreForSubmission(0, selectedSubmission.id) : null}
      onSave={async (updatedSubmission) => {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        const apiUrl = import.meta.env.VITE_API_URL;
        const response = await fetch(`${apiUrl}/admin/jv-submissions/${updatedSubmission.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: updatedSubmission.status,
            assignedTo: updatedSubmission.assignedTo,
            notes: updatedSubmission.notes,
            maoReportUrls: updatedSubmission.maoReportUrls,
            propertyPhotoUrls: updatedSubmission.propertyPhotoUrls,
            additionalDocUrls: updatedSubmission.additionalDocUrls,
            // Closing Details (admin-only; partner sees them once filled, '' clears)
            propertyListingLink: updatedSubmission.propertyListingLink,
            propertySaleAmount: updatedSubmission.propertySaleAmount
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update submission');
        }

        // Refresh the submissions list in the background. fetchJVSubmissions() pages through
        // every submission (100 per request), which can take 30–90s on dev; awaiting it here
        // made every modal save ("Saved", status-bar Save, Closing Details) look stuck until
        // the whole list had reloaded, even though the PUT had long succeeded.
        void fetchJVSubmissions();
      }}
    />
  );

  if (loading && (users.length === 0 && activities.length === 0)) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tdb-accent mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
        {submissionDetailsModal}
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <span>Admin Portal</span>
          </h1>
          <p className="text-gray-600 mt-1">{isSuperAdmin ? 'Super admin access' : 'Manage regular users'}</p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === 'users' && (
            <>
              <button
                onClick={() => {
                  setCreateModalType('user');
                  setShowCreateModal(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
              >
                <UserPlus className="w-4 h-4" />
                <span>Create Member</span>
              </button>
              {(userGroups.includes('admin_manager') || userGroups.includes('super_admin')) && (
                <button
                  onClick={() => {
                    setCreateModalType('admin');
                    setShowCreateModal(true);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
                >
                  <Shield className="w-4 h-4" />
                  <span>Create Admin</span>
                </button>
              )}
            </>
          )}
          <button
            onClick={() => activeTab === 'users' ? fetchUsers() : fetchUserActivity()}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b-2 border-gray-200">
        <div className="flex space-x-1">
          <button
            onClick={() => handleTabChange('users')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-tdb-accent text-tdb-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>User Management</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('jv-submissions')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'jv-submissions'
                ? 'border-tdb-accent text-tdb-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5" />
              <span>Deals</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('activity')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-tdb-accent text-tdb-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>User Activity</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('dfpb-orders')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'dfpb-orders'
                ? 'border-tdb-accent text-tdb-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FileSpreadsheet className="w-5 h-5" />
              <span>DFG Orders</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('escalations')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'escalations'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Escalations</span>
              {escalations.filter(e => e.status === 'open').length > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {escalations.filter(e => e.status === 'open').length}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Status Cards (only for JV Submissions) */}
      {activeTab === 'jv-submissions' && (
        <>
          {dealHealthLoading && (
            <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading deal scores…
            </div>
          )}
          {dealHealthError && !dealHealthLoading && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="inline w-4 h-4 mr-1" />
              Deal scores unavailable: {dealHealthError}
            </div>
          )}
          {dealHealth && (
            <>
              <PipelineValueHero
                total={dealHealth.pipelineValue.total}
                dealCount={dealHealth.pipelineValue.dealCount}
                cutoffDate={dealHealth.pipelineValue.cutoffDate}
                byStage={dealHealth.pipelineValue.byStage}
              />

              <TopTenPanel
                deals={dealHealth.topTen}
                onOpen={(id) => {
                  const sub = jvSubmissions.find((s: any) => s.id === id);
                  if (sub) {
                    setSelectedSubmission(sub);
                    setSubmissionModalMode('view');
                  }
                }}
              />
            </>
          )}

          <StatusCards
            statusCounts={dynamicStatusCounts}
            activeStatus={statusFilter}
            onStatusClick={handleStatusCardClick}
          />
        </>
      )}

      {/* Filters and Search */}
      {activeTab !== 'dfpb-orders' && (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col space-y-4">
          {/* Top Row: Filters (only for JV Submissions) */}
          {activeTab === 'jv-submissions' && (
            <div className="flex flex-wrap items-center gap-4">
            <>
              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-semibold text-gray-700">Status:</label>
                <select
                  data-testid="admin-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                >
                  <option value="all">All Statuses (excl. Archived)</option>
                  <option value="new">New</option>
                  <option value="follow-up">Follow-Up</option>
                  <option value="hot-lead">Hot Lead</option>
                  <option value="option-sent">Option Sent</option>
                  <option value="contract-pending">Contract Pending</option>
                  <option value="under-contract">Under Contract</option>
                  <option value="appointment-scheduled">Appointment Scheduled</option>
                  <option value="closing-schedule">Closing Schedule</option>
                  <option value="student-action-required">Student Action Required</option>
                  <option value="closed">Closed</option>
                  <option value="cold">Cold</option>
                  <option value="dead">Dead</option>
                  <option value="do-not-contact">Do Not Contact</option>
                  <option value="bad-phone-number">Bad Phone Number</option>
                  <option value="warm-lead">Warm Lead</option>
                  <option value="unresponsive">Unresponsive</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Filter by User/Partner */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-semibold text-gray-700">Partner:</label>
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                >
                  <option value="all">All Partners</option>
                  {uniquePartners.map(partner => (
                    <option key={partner.email} value={partner.email}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Assigned To Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-semibold text-gray-700">Assigned To:</label>
                <select
                  value={assignedToFilter}
                  onChange={(e) => setAssignedToFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent"
                >
                  <option value="me">Assigned to Me</option>
                  <option value="all">All Assignments</option>
                  <option value="unassigned">Unassigned</option>
                  {adminUsers.filter(u => u.enabled).map(user => (
                    <option key={user.email} value={user.email}>
                      {user.name || user.email}
                    </option>
                  ))}
                </select>
              </div>
            </>
            </div>
          )}

          {/* Bottom Row: Search Bar (Full Width) */}
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={
                activeTab === 'users' ? 'Search users by email, group, or tag...' :
                activeTab === 'jv-submissions' ? 'Search by partner name, property address, phone...' :
                'Search by email or endpoint...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all text-base"
            />
          </div>

          {/* Export Button (only for JV Submissions) */}
          {activeTab === 'jv-submissions' && filteredJVSubmissions.length > 0 && (
            <button
              onClick={() => {
                // Convert filtered submissions to CSV
                const headers = ['Partner Name', 'Partner Email', 'Property Address', 'Phone', 'Status', 'Assigned To', 'Created Date'];
                const csvRows = [headers.join(',')];

                filteredJVSubmissions.forEach(sub => {
                  const row = [
                    `"${sub.partnerFirstName} ${sub.partnerLastName}"`,
                    sub.partnerEmail,
                    `"${sub.propertyStreet}, ${sub.propertyCity}, ${sub.propertyState}"`,
                    sub.partnerPhone || '',
                    sub.status,
                    sub.assignedTo || 'Unassigned',
                    sub.createdAt
                  ];
                  csvRows.push(row.join(','));
                });

                const csvContent = csvRows.join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `jv-submissions-export-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center space-x-2 whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              <span>Export ({filteredJVSubmissions.length})</span>
            </button>
          )}

          {/* Bulk Actions (only for JV Submissions with selections) */}
          {activeTab === 'jv-submissions' && selectedSubmissions.size > 0 && (
            <button
              onClick={async () => {
                if (!confirm(`Delete ${selectedSubmissions.size} submission(s)? This cannot be undone.`)) return;

                try {
                  const session = await fetchAuthSession();
                  const idToken = session.tokens?.idToken?.toString();
                  const apiUrl = import.meta.env.VITE_API_URL;

                  // Delete each selected submission
                  for (const submissionId of Array.from(selectedSubmissions)) {
                    await fetch(`${apiUrl}/admin/jv-submissions/${submissionId}`, {
                      method: 'DELETE',
                      headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                      }
                    });
                  }

                  // Clear selections and refresh
                  setSelectedSubmissions(new Set());
                  await fetchJVSubmissions();
                } catch (err) {
                  alert('Failed to delete submissions');
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete ({selectedSubmissions.size})</span>
            </button>
          )}
        </div>
      </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 text-red-800">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">{error}</span>
          </div>
        </div>
      )}

      {/* JV Submissions Table */}
      {activeTab === 'jv-submissions' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions.size === filteredJVSubmissions.length && filteredJVSubmissions.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-tdb-accent focus:ring-tdb-accent border-gray-300 rounded"
                    />
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('partnerName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Partner Name</span>
                      {sortColumn === 'partnerName' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('propertyAddress')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Property Address</span>
                      {sortColumn === 'propertyAddress' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('phone')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Phone</span>
                      {sortColumn === 'phone' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    <span title="Wholesale · Rentback deal scores (0–100). Green = hot, amber = warm, gray = cold. Highlighted border = recommended strategy.">Score W·R ⓘ</span>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Assigned To</th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Submitted</span>
                      {sortColumn === 'createdAt' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredJVSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <Building2 className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No JV submissions found</p>
                        <p className="text-xs text-gray-400">
                          {statusFilter !== 'all' || searchQuery ? 'Try adjusting your filters' : 'Submissions will appear here once clients submit properties'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredJVSubmissions.map((submission, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setSubmissionModalMode('view');
                      }}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedSubmissions.has(submission.id)}
                          onChange={() => toggleSelectSubmission(submission.id)}
                          className="w-4 h-4 text-tdb-accent focus:ring-tdb-accent border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {submission.partnerFirstName} {submission.partnerLastName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-tdb-accent hover:underline cursor-pointer">
                          {submission.propertyStreet}, {submission.propertyCity}, {submission.propertyState}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">{submission.partnerPhone}</div>
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const sc = scoreForSubmission(index, submission.id);
                          if (!sc) return <span className="text-xs text-gray-400">—</span>;
                          return (
                            <div className="flex items-center gap-2">
                              <DualScorePillar
                                wholesale={sc.scores.wholesale}
                                rentback={sc.scores.rentback}
                                recommended={sc.recommendedStrategy}
                                size="sm"
                              />
                              <HealthBadge health={sc.health} />
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          title={STATUS_TOOLTIP[submission.status] || submission.status}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                        >
                          {submission.status || 'New'}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        {editingAssignmentId === submission.id ? (
                          <div className="min-w-[200px]">
                            <AssigneeSelector
                              assignedTo={submission.assignedTo || ''}
                              onChange={(email) => handleInlineAssignment(submission.id, email)}
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingAssignmentId(submission.id)}
                            className="text-sm text-left hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full"
                          >
                            {submission.assignedTo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {submission.assignedTo.split('@')[0]}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic text-xs">Unassigned</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {new Date(submission.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div className="group relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSubmission(submission);
                                setSubmissionModalMode('view');
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              View Submission
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                          <div className="group relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPropertyAnalysisSubmission(submission);
                              }}
                              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                            >
                              <BarChart2 className="w-4 h-4" />
                            </button>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              Property Analysis
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Management Table */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Seen</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Tags</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <Users className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No users found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setSelectedUser(user);
                        setShowUserActionsModal(true);
                      }}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{user.email || user.username}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {user.enabled ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className={`text-sm font-semibold ${user.enabled ? 'text-green-600' : 'text-red-600'}`}>
                            {user.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const userEmail = user.email || user.username;
                          const status = onlineStatus[userEmail];

                          if (!status) {
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full border-2 border-gray-400"></div>
                                <span className="text-xs text-gray-500">Never</span>
                              </div>
                            );
                          }

                          const lastActivity = new Date(status.lastActivity);
                          const now = new Date();
                          const diffMs = now.getTime() - lastActivity.getTime();
                          const diffMin = Math.floor(diffMs / 60000);

                          let statusColor = '';
                          let statusText = '';

                          // Handle invalid/future timestamps
                          if (diffMin < 0) {
                            statusColor = 'bg-green-500';
                            statusText = 'Online now';
                          } else if (status.status === 'online') {
                            statusColor = 'bg-green-500';
                            statusText = 'Online';
                          } else if (status.status === 'away') {
                            statusColor = 'bg-yellow-500';
                            statusText = diffMin < 60 ? `${diffMin}m ago` : `${Math.floor(diffMin / 60)}h ago`;
                          } else {
                            statusColor = 'border-2 border-gray-400';
                            if (diffMin < 60) {
                              statusText = `${diffMin}m ago`;
                            } else if (diffMin < 1440) {
                              statusText = `${Math.floor(diffMin / 60)}h ago`;
                            } else {
                              statusText = `${Math.floor(diffMin / 1440)}d ago`;
                            }
                          }

                          return (
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${statusColor}`}></div>
                              <span className="text-xs text-gray-600 font-medium">{statusText}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.tags && user.tags.length > 0 ? (
                            user.tags.map((tag, i) => (
                              <span
                                key={i}
                                className={`text-xs font-semibold px-2 py-1 rounded ${
                                  tag === 'DFPB' ? 'bg-teal-100 text-teal-800' :
                                  tag === 'BPG' ? 'bg-orange-100 text-orange-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{formatDate(user.created)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {canManageUser(user) ? (
                          <span className="text-xs text-tdb-accent font-semibold cursor-pointer hover:underline">
                            Manage →
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 font-semibold">No access</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Activity Table */}
      {activeTab === 'activity' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Activity</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Endpoint</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredActivities.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <Activity className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No activity found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredActivities.map((activity, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{activity.user_email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{formatDate(activity.last_activity)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono text-gray-700">{activity.last_endpoint}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {activity.last_method}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-semibold ${getStatusColor(activity.status_code)}`}>
                          {activity.status_code}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DFPB Orders */}
      {activeTab === 'dfpb-orders' && <DFPBOrders initialOrderId={initialParams?.orderId} />}

      {/* Escalations */}
      {activeTab === 'escalations' && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">Partner Escalations</h2>
              {escalations.filter(e => e.status === 'open').length > 0 && (
                <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-full">
                  {escalations.filter(e => e.status === 'open').length} open
                </span>
              )}
            </div>
            <button onClick={fetchEscalations} className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 text-sm font-medium">
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
          {escLoading ? (
            <div className="p-12 text-center text-gray-500">Loading escalations…</div>
          ) : escError ? (
            <div className="p-12 text-center text-red-600" data-testid="escalations-error">{escError}</div>
          ) : escalations.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No escalations found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left">Status</th>
                    <th scope="col" className="px-6 py-3 text-left">Subject / Message</th>
                    <th scope="col" className="px-6 py-3 text-left">Partner</th>
                    <th scope="col" className="px-6 py-3 text-left">Property</th>
                    <th scope="col" className="px-6 py-3 text-left">Submitted</th>
                    <th scope="col" className="px-6 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {escalations.map(esc => (
                    <tr key={esc.escalationId} className={esc.status === 'open' ? 'bg-red-50' : 'bg-white'}>
                      <td className="px-6 py-4">
                        {esc.status === 'open' ? (
                          <span title="Open — this escalation has not yet been acknowledged by an admin. Requires immediate attention." className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Open</span>
                        ) : (
                          <span title="Acknowledged — an admin has reviewed and acknowledged this escalation." className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Acknowledged</span>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-sm">
                        <div className="font-medium text-gray-900">{esc.subject}</div>
                        <div className="text-gray-600 text-xs mt-1 whitespace-pre-wrap">{esc.details}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{esc.partnerName}</div>
                        <div className="text-gray-500 text-xs">{esc.partnerEmail}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 max-w-xs">{esc.propertyAddress || '—'}</td>
                      <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                        {new Date(esc.submittedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {esc.submissionId && (
                            <button
                              onClick={() => {
                                handleTabChange('jv-submissions');
                                const sub = jvSubmissions.find((s: any) => s.id === esc.submissionId);
                                if (sub) {
                                  setSelectedSubmission(sub);
                                  setSubmissionModalMode('view');
                                } else {
                                  window.history.replaceState({}, '', `#/admin-portal?submissionId=${esc.submissionId}`);
                                }
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                            >
                              View JV Entry
                            </button>
                          )}
                          {esc.status === 'open' ? (
                            <button
                              onClick={() => acknowledgeEscalation(esc.submissionId, esc.escalationId)}
                              disabled={escalationBusy === esc.escalationId}
                              className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {escalationBusy === esc.escalationId ? 'Acknowledging…' : 'Acknowledge'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {esc.acknowledgedBy ? `by ${esc.acknowledgedBy}` : 'Done'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createUser}
        isSuperAdmin={isSuperAdmin}
        type={createModalType}
      />

      {/* Property Analysis Modal */}
      <PropertyAnalysisModal
        submission={propertyAnalysisSubmission}
        isOpen={!!propertyAnalysisSubmission}
        onClose={() => setPropertyAnalysisSubmission(null)}
      />

      {/* JV Submission Details Modal */}
      {submissionDetailsModal}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Confirm {confirmAction.type === 'suspend' ? 'Suspend' : 'Unsuspend'} User
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to {confirmAction.type} <strong>{confirmAction.email}</strong>?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmUserAction}
                className={`px-4 py-2 text-white rounded-lg font-semibold ${
                  confirmAction.type === 'suspend'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {confirmAction.type === 'suspend' ? 'Suspend' : 'Unsuspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Actions Modal */}
      <UserActionsModal
        isOpen={showUserActionsModal}
        onClose={() => {
          setShowUserActionsModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        adminFirstName={adminFirstName}
        onSendEmail={sendUserEmail}
        onSuspend={disableUser}
        onUnsuspend={enableUser}
        onResetPassword={resetPassword}
        onResendLogin={resendLogin}
        canManageUser={selectedUser ? canManageUser(selectedUser) : false}
      />

      {/* Magic login link — copy/paste for the admin (also emailed to the user) */}
      {magicLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Magic login link</h3>
              <button onClick={() => setMagicLink(null)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-700 mb-4">
              A one-click login link was emailed to <strong>{magicLink.email}</strong>. You can also copy it
              below and send it to them directly.
            </p>
            <div className="flex items-center space-x-2 mb-3">
              <input
                readOnly
                data-testid="admin-magic-link-input"
                value={magicLink.link}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono truncate"
              />
              <button
                data-testid="admin-magic-link-copy"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(magicLink.link);
                    setLinkCopied(true);
                    showSuccess('Link copied');
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch {
                    showError('Copy failed — select the text and copy manually');
                  }
                }}
                className="px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] font-semibold flex items-center space-x-2 whitespace-nowrap"
              >
                {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{linkCopied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
              ⚠️ This link signs the user in and lets them set a password — treat it like a password.
              It expires in 7 days and can be used only once.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setMagicLink(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
